import { Card, cardName } from '../card_index';
import { DropListItem, Hints, IDropElement } from '../functions/hints';
import { Record } from 'immutable';
import { Orientation } from '../utils/detectOrientation';

export interface ICard extends Card {
  draggable?: boolean;
  state?: CardState;
  index?: pileIndex | slotIndex;
  isLastCard?: boolean;
  isSingleCard?: boolean;
  cardIndex?: number;
  puttedWrong?: boolean;
  pileSize?: number;
}

export type pileIndex = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7;

export type slotIndex = 0 | 1 | 2 | 3;

export type IPile = ICard[];
export type ILaid = IPile[];

export enum CardState {
  home = 'home',
  slots = 'slot',
  laid = 'laid',
}

export type requestState = {
  home: ILaid;
  slots: (ICard | null)[];
  laid: number[][];
};

export interface IGameState {
  home: ILaid;
  slots: (ICard | null)[];
  laid: ILaid;
}

export type ICardsRenderInfo = {
  width: number;
  height: number;
  corner: number;
  normalShadow: number;
  offset: number;
};

export type history = {
  state: IGameState;
  action: Action;
  undoPosition?: undoPosition;
}[];

export type IGameData = {
  steps: number;
  scores: number;
  startTime: number;
  cardsAtHome: number;
  isWin: boolean;
};

export type IModals = {
  newGame: {
    isVisible: boolean;
  };

  restart: {
    isVisible: boolean;
  };

  rules: {
    isVisible: boolean;
  };
};

export type ICardOverflowMessage = {
  isVisible: boolean;
  maxCards: number;
  needCards: number;
};

export interface IGame {
  state: IGameState;
  data: IGameData;
}

export interface IState {
  id: number;
  gameState: IGameState;
  cardsRenderInfo: ICardsRenderInfo;
  dropElements: IDropElement[];
  hints: Hints;
  settings: Settings;
  history: history;
  gameData: IGameData;
  modals: IModals;
  numberOfEmptySlots: number;
  numberOfEmptyPiles: number;
  cardsOverflowMessage: ICardOverflowMessage;
  hintNumber: number;
  orientation?: Orientation | void;
  size: number;
  volume: boolean;
}

export interface RecordState extends Record<IState> {}

export type Settings = {
  listening: boolean;
  superMove: SuperMove | false;
  superMoveSequence: number;
};

export interface SuperMove extends DropListItem {
  homeSlot: number;
}

export type undoPosition = {
  name: cardName;
  x: number;
  y: number;
};

export enum ActionType {
  DropFromLaidToLaid = 'DropFromLaidToLaid',
  DropFromLaidToSlots = 'DropFromLaidToSlots',
  DropFromSlotsToLaid = 'DropFromSlotsToLaid',
  DropFromSlotsToSlots = 'DropFromSlotsToSlots',
  DropFromSlotsToHome = 'DropFromSlotsToHome',
  DropFromLaidToHome = 'DropFromLaidToHome',

  SetListening = 'SetListening',
  Undo = 'Undo',
  ToggleModal = 'ToggleModal',
  NewGame = 'NewGame',
  RestartGame = 'RestartGame',
  ShowCardsOverflowMessage = 'ShowCardsOverflowMessage',
  IncreaseHintNumber = 'IncreaseHintNumber',
  SetOrientation = 'SetOrientation',
  ChangeScores = 'ChangeScores',
  ChangeVolume = 'ChangeVolume',
}

export type DropFromLaidToHomePayload = {
  pileNumber: number;
  homeSlotNumber: number;
  undoPosition?: undoPosition;
};

export type DropFromSlotsToHomePayload = {
  slotNumber: number;
  homeSlotNumber: number;
  undoPosition?: undoPosition;
};

export type DropFromLaidToLaidPayload = {
  pileOneNumber: number;
  pileTwoNumber: number;
  splitNumber: number;
  undoPosition?: undoPosition;
};

export type LaidSlotsDropPayload = {
  pileNumber: number;
  slotNumber: number;
  undoPosition?: undoPosition;
};

export type DropFromSlotsToSlotsPayload = {
  slotOneNumber: number;
  slotTwoNumber: number;
  undoPosition?: undoPosition;
};

export interface IDropFromLaidToLaidAction {
  type: ActionType.DropFromLaidToLaid;
  payload: DropFromLaidToLaidPayload;
}

export interface IDropFromLaidToSlotsAction {
  type: ActionType.DropFromLaidToSlots;
  payload: LaidSlotsDropPayload;
}

export interface IDropFromSlotsToLaidAction {
  type: ActionType.DropFromSlotsToLaid;
  payload: LaidSlotsDropPayload;
}

export interface IDropFromSlotsToSlotsAction {
  type: ActionType.DropFromSlotsToSlots;
  payload: DropFromSlotsToSlotsPayload;
}

export interface IDropFromSlotsToHomeAction {
  type: ActionType.DropFromSlotsToHome;
  payload: DropFromSlotsToHomePayload;
}

export interface IDropFromLaidToHomeAction {
  type: ActionType.DropFromLaidToHome;
  payload: DropFromLaidToHomePayload;
}

export interface IUndo {
  type: ActionType.Undo;
}

export interface IToggleModal {
  type: ActionType.ToggleModal;
  payload: {
    name: string;
  };
}

export interface INewGame {
  type: ActionType.NewGame;
}

export interface IRestartGame {
  type: ActionType.RestartGame;
}

export interface IShowCardsOverflowMessage {
  type: ActionType.ShowCardsOverflowMessage;
  payload: {
    visible: boolean;
    maxCards: number;
    needCards: number;
  };
}

export interface IncreaseHintNumber {
  type: ActionType.IncreaseHintNumber;
}

export interface ISetOrientation {
  type: ActionType.SetOrientation;
  payload: {
    orientation: Orientation;
    contentWidth: number;
  };
}

export interface IChangeScores {
  type: ActionType.ChangeScores;
  payload: {
    scores: number;
  };
}

export interface IChangeVolume {
  type: ActionType.ChangeVolume;
}

export type Action =
  | IDropFromLaidToLaidAction
  | IDropFromLaidToSlotsAction
  | IDropFromSlotsToLaidAction
  | IDropFromSlotsToSlotsAction
  | IDropFromSlotsToHomeAction
  | IDropFromLaidToHomeAction
  | IUndo
  | IToggleModal
  | INewGame
  | IShowCardsOverflowMessage
  | IncreaseHintNumber
  | ISetOrientation
  | IRestartGame
  | IChangeScores
  | IChangeVolume;
